// Shared recompute helper for the bill's net-payable total when ANY benefit
// (coupon / scheme / loyalty points) changes. The three panels used to do
// their own `nextTotal = original - own_discount` math, which dropped sibling
// discounts whenever a second benefit was applied or removed. Now every
// apply/clear path calls recomputeBillTotal so the total is always
//     original - couponDiscount - schemeDiscount - pointsDiscount
//
// `override` is necessary because Formik's setFieldValue is async-batched —
// when a panel has just called setFieldValue("couponDiscount", line) and then
// calls this helper, `values.couponDiscount` is still the OLD value. The
// panel passes its just-applied value via override.{coupon|scheme|points}.

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

export function recomputeBillTotal({
  values,
  setFieldValue,
  prefixName,
  override = {},
}) {
  if (!values || typeof setFieldValue !== "function") return;
  const totalKey = `${prefixName}_total_amount`;
  const origKey = `${prefixName}_original_total_amount`;
  if (values[totalKey] == null) return;

  // First benefit application records the pre-benefit total as the anchor.
  // Subsequent recomputes work from that anchor — never from the live total —
  // so we don't accumulate drift across stacked discounts.
  const original =
    values[origKey] != null
      ? Number(values[origKey])
      : Number(values[totalKey]);
  if (values[origKey] == null) {
    setFieldValue(origKey, original);
  }

  const coupon = Number(
    (override.coupon !== undefined
      ? override.coupon
      : values?.couponDiscount?.discount_amount) || 0
  );
  const scheme = Number(
    (override.scheme !== undefined
      ? override.scheme
      : values?.schemeDiscount?.discount_amount) || 0
  );
  const points = Number(
    (override.points !== undefined
      ? override.points
      : values?.pointsDiscount?.discount_amount) || 0
  );

  const next = round2(Math.max(0, original - coupon - scheme - points));
  setFieldValue(totalKey, next);

  // If every benefit has been cleared, drop the anchor so the next bill's
  // first apply re-snapshots from a fresh starting total.
  if (coupon === 0 && scheme === 0 && points === 0) {
    setFieldValue(origKey, null);
  }
}
